import React, { useCallback, useEffect, useState } from 'react';
import {
  listMerchantVouchers,
  authenticateVoucher,
  redeemVoucherCode,
} from '../../lib/rewardVoucherApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnRow,
  Card,
  Table,
  Badge,
  ErrorText,
  Field,
} from '../../styles/adminStyles';

function voucherStatusBadge(status) {
  if (status === 'authenticated') return 'active';
  if (status === 'rejected') return 'ended';
  return 'draft';
}

function voucherValueLabel(v, freeGiftLabel) {
  if (v.couponType === 'percentage' && v.discountPercent != null) return `${v.discountPercent}% off`;
  if (v.valueCents != null) return `$${(Number(v.valueCents) / 100).toFixed(2)}`;
  return freeGiftLabel;
}

export default function VoucherAuthPage() {
  const { t } = usePortalT();
  const statusLabel = (status) => {
    if (status === 'authenticated') return t('voucherAuth.statusAuthenticated');
    if (status === 'rejected') return t('voucherAuth.statusRejected');
    if (status === 'pending') return t('voucherAuth.statusPending');
    return status || '—';
  };
  const TABS = [
    { key: 'pending', label: t('voucherAuth.tabPending') },
    { key: 'authenticated', label: t('voucherAuth.tabAuthenticated') },
    { key: 'all', label: t('voucherAuth.tabAll') },
  ];
  const [tab, setTab] = useState('pending');
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Redemption (consume by code)
  const [code, setCode] = useState('');
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setVouchers(await listMerchantVouchers(tab, 100));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [tab, t]);

  useEffect(() => { load(); }, [load]);

  const handleAuthenticate = async (voucherId, approve) => {
    setBusyId(voucherId);
    setError(null);
    let note = null;
    if (!approve) {
      // eslint-disable-next-line no-alert
      note = window.prompt(t('voucherAuth.rejectPrompt')) || null;
    }
    const res = await authenticateVoucher(voucherId, approve, note);
    setBusyId(null);
    if (res.success) {
      await load();
    } else {
      setError(res.error || t('common.actionFailed'));
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    const c = code.trim();
    if (!c) return;
    setRedeemBusy(true);
    setRedeemMsg(null);
    const res = await redeemVoucherCode(c, null);
    setRedeemBusy(false);
    if (res.success) {
      setRedeemMsg({ ok: true, text: t('voucherAuth.redeemOk', { title: res.voucherTitle || t('voucherAuth.voucherFallback') }) });
      setCode('');
    } else {
      const map = {
        not_found: t('voucherAuth.errNotFound'),
        not_authorized: t('voucherAuth.errNotAuthorized'),
        not_authenticated: t('voucherAuth.errNotAuthenticated'),
        already_redeemed: t('voucherAuth.errAlreadyRedeemed'),
        expired: t('voucherAuth.errExpired'),
      };
      setRedeemMsg({ ok: false, text: map[res.error] || res.error || t('voucherAuth.redeemFailed') });
    }
  };

  return (
    <>
      <PageTitle>{t('voucherAuth.title')}</PageTitle>
      <PageSubtitle>
        {t('voucherAuth.subtitle')}
      </PageSubtitle>

      <Card>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>{t('voucherAuth.inStore')}</h3>
        <form onSubmit={handleRedeem}>
          <Field>
            {t('voucherAuth.codeLabel')}
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="RV-1A2B-3C4D"
              autoCapitalize="characters"
            />
          </Field>
          <BtnRow style={{ marginTop: 0 }}>
            <Btn $primary type="submit" disabled={redeemBusy || !code.trim()}>
              {redeemBusy ? t('voucherAuth.redeeming') : t('voucherAuth.redeem')}
            </Btn>
          </BtnRow>
        </form>
        {redeemMsg ? (
          <p style={{ marginTop: 12, fontWeight: 700, color: redeemMsg.ok ? '#15803d' : '#b91c1c' }}>
            {redeemMsg.text}
          </p>
        ) : null}
      </Card>

      <BtnRow style={{ marginTop: 24, marginBottom: 12 }}>
        {TABS.map((tabItem) => (
          <Btn key={tabItem.key} $primary={tab === tabItem.key} type="button" onClick={() => setTab(tabItem.key)}>
            {tabItem.label}
          </Btn>
        ))}
      </BtnRow>

      {error ? <ErrorText>{error}</ErrorText> : null}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <p style={{ padding: 18 }}>{t('common.loading')}</p>
        ) : vouchers.length === 0 ? (
          <p style={{ padding: 18, color: '#64748b' }}>{t('voucherAuth.empty')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t('voucherAuth.colVoucher')}</th>
                <th>{t('voucherAuth.colRoute')}</th>
                <th>{t('voucherAuth.colCreator')}</th>
                <th>{t('voucherAuth.colValue')}</th>
                <th>{t('voucherAuth.colQuota')}</th>
                <th>{t('common.status')}</th>
                <th>{t('voucherAuth.colAction')}</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map((v) => (
                <tr key={v.id}>
                  <td style={{ fontWeight: 700 }}>{v.title}</td>
                  <td>{v.scriptTitle || '—'}</td>
                  <td>{v.creatorName || '—'}</td>
                  <td>{voucherValueLabel(v, t('voucherAuth.freeGift'))}</td>
                  <td>
                    {v.winnerLimit ?? '∞'} / {v.claimedCount ?? 0} / {v.redeemedCount ?? 0}
                  </td>
                  <td><Badge $status={voucherStatusBadge(v.status)}>{statusLabel(v.status)}</Badge></td>
                  <td>
                    {v.status === 'pending' ? (
                      <BtnRow style={{ marginTop: 0 }}>
                        <Btn
                          $primary
                          type="button"
                          disabled={busyId === v.id}
                          onClick={() => handleAuthenticate(v.id, true)}
                        >
                          {t('voucherAuth.approve')}
                        </Btn>
                        <Btn
                          type="button"
                          disabled={busyId === v.id}
                          onClick={() => handleAuthenticate(v.id, false)}
                        >
                          {t('voucherAuth.reject')}
                        </Btn>
                      </BtnRow>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
